import { isEmpty, noop, isFunction } from 'underscore';
import ko from 'knockout';
import React, { useCallback, useEffect, useState, useRef, useContext, useMemo } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import BackboneCallToActionAdapter from '@bingads-webui-campaign-legacy/adextension-actionlink/view/action-type-adapter';
import BackboneCallToActionLanguageAdapter from '@bingads-webui-campaign-legacy/adextension-actionlink/view/language-adapter';

import { CallToActionAriaText } from './call-to-action-aria-text';
import { CallToActionText } from './call-to-action-text';
import { getStyles } from './style';
import { getLanguage } from './locale-to-language';

// Wraps legacy @bingads-webui-component-legacy/adextension-actionlink/view/action-type-adapter component
export const LegacyCallToAction = React.memo(({
  id, className, value, setNewCTACallback, languageValue, ctaText, ctaAriaText, onChange, locale, permissions, isPmaxAd, isBoostScenario, isBoostCustomCTAEnabled, isCustomCTAEnabled, valueText,
}) => {
  const view = useRef();

  const i18n = useContext(I18nContext);
  const languageView = useRef();
  const isBoostAccount = permissions && permissions.IsAmplifyEnabled;
  const isBoostCTAAriaTextEnabled = isBoostAccount && permissions && permissions.IsBoostCTAAriaTextEnabled;

  let container;

  let languageContainer;

  const [cta, setCta] = useState(null);
  const [ctaString, setCtaString] = useState(null);
  const [language, setLanguage] = useState(null);

  const viewModel = useMemo(() => ({
    ActionType: ko.observable(!isEmpty(value) ? value : null),
    Language: ko.observable(languageValue || getLanguage(permissions, locale)),
  }), [languageValue, locale, permissions, value]);

  const updateCTA = (selectedOption) => {
    setCta(selectedOption.id);
    setCtaString(selectedOption.value);
  };

  const updateLanguage = (selectedOption) => {
    setLanguage(selectedOption.id);
  };

  useEffect(() => {
    if (setNewCTACallback) {
      setNewCTACallback(() => ({ CallToAction, CallToActionLanguage }) => {
        if (CallToActionLanguage) {
          viewModel.Language = ko.observable(CallToActionLanguage);
          viewModel.ActionType(CallToAction);
          view.current.observable(CallToAction);
          view.current.render();

          if (languageView && languageView.current && isFunction(languageView.current.render)) {
            languageView.current.observable = viewModel.Language;
            languageView.current.render();
          }
        } else {
          view.current.observable(CallToAction);
          view.current.render();
        }
      });
    }
  }, [setNewCTACallback, viewModel]);

  useEffect(() => {
    const actionTypeAdapter = new BackboneCallToActionAdapter({
      observable: viewModel.ActionType,
      viewModel,
      callback: (selectedOption) => {
        updateCTA(selectedOption);
      },
      isPmaxAd,
      isBoostScenario,
      isBoostCustomCTAEnabled,
      isCustomCTAEnabled,
      defaultActionText: valueText,
    });

    const languageAdapter = new BackboneCallToActionLanguageAdapter({
      observable: viewModel.Language,
      viewModel,
      callback: (selectedOption) => {
        updateLanguage(selectedOption);
      },
      isBoostScenario,
      isBoostCustomCTAEnabled,
    });

    if (isBoostScenario) {
      languageView.current = languageAdapter;
      languageView.current.render();
      languageView.current.$el.appendTo(languageContainer);
    }

    view.current = actionTypeAdapter;

    view.current.render();
    view.current.$el.appendTo(container);


    return () => {
      view.current.remove();
    };
    // We want the view to be created only once,
    // hence ignoring all dependencies of this "effect".
    // This means that the view will ignore changes to all props.
    // If need be to make view pick props updates, the view
    // need to provide a way to communicate such updates to it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => {
      // for pmax ad, maintain same logic
      if (!isBoostScenario && cta !== null) {
        onChange(cta, ctaString);
      }

      // for display plus boost campaigns add language and cta text
      if (isBoostScenario) {
        if (cta !== null && language !== null) {
          onChange({ cta, language });
        }
      }
    }
    , [cta, ctaString, isBoostScenario, language, onChange]
  );

  const classes = useDefaultStyles(getStyles);

  const onGlobalShortcut = useCallback((e) => {
    if (e.key === 'Escape' && document.getElementsByClassName('dropdown-search-selector__dropdown open').length > 0) {
      e.preventDefault();
      document.getElementsByClassName('row btn btn-default dropdown-toggle')[0].click();
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keyup', onGlobalShortcut);
    return () => {
      document.removeEventListener('keyup', onGlobalShortcut);
    };
  }, [i18n, onGlobalShortcut]);
  return (
    <>
      <div
        id={`language-${id}`}
        className={classnames(className, classes.callToActionContainer)}
      >
        <div ref={(ref) => { languageContainer = ref; }} />
      </div>
      <div
        id={id}
        className={classnames(className, classes.callToActionContainer)}
      >
        <div ref={(ref) => { container = ref; }} />
      </div>
      <div
        id={`custom-test-${id}`}
        className={classnames(className, classes.callToActionContainer)}
      >
        { /* this is a validated contrl  so it directly reports to parent validation group */}
        {cta === 'CustomText' && <CallToActionText i18n={i18n} defaultValue={ctaText} />}
      </div>

      {
        isBoostCTAAriaTextEnabled && (
          <div id="call-to-action-aria-text" className={classnames(className, classes.callToActionContainer)}>
            <CallToActionAriaText i18n={i18n} value={ctaAriaText} />
          </div>
        )
      }
    </>
  );
});

LegacyCallToAction.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,

  locale: PropTypes.string.isRequired,
  permissions: PropTypes.shape({}).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func,
  isPmaxAd: PropTypes.bool,
  isBoostScenario: PropTypes.bool,
  isBoostCustomCTAEnabled: PropTypes.bool,
  ctaText: PropTypes.string,
  ctaAriaText: PropTypes.string,
  languageValue: PropTypes.string,
  isCustomCTAEnabled: PropTypes.bool,
  valueText: PropTypes.string,
  setNewCTACallback: PropTypes.func,
};

LegacyCallToAction.defaultProps = {
  id: undefined,
  className: undefined,
  value: undefined,
  onChange: noop,
  isPmaxAd: false,
  isBoostScenario: false,
  isBoostCustomCTAEnabled: false,
  ctaText: null,
  ctaAriaText: null,
  languageValue: null,
  isCustomCTAEnabled: false,
  valueText: '',
  setNewCTACallback: null,
};
